import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch, MutableRefObject, SetStateAction } from 'react';
import { Spin, Tooltip } from 'antd';
import quoteServices from '../../../../../services/oneButtonServices/quoteServices';
import { useOneButtonStore } from '../../../../../store/oneButtonStore';
import {
    buildDesignEpcByPartner,
    buildTreeCutPartnerColumnCells,
    mainCardPricingScenarioKey,
    partnerEpcUnavailableDetailFromApi,
    resolveUtilityCompanyCode,
    type DesignEpcCell,
    type EpcDisplayPartner,
    type MainCardPartnerCellState,
} from '../utils/finance';

interface UseMainCardScenarioPricingArgs {
    solarUtilityData: any;
    priceQuoteOverrides: any;
    mainCardPricingBodyRef: MutableRefObject<Record<string, unknown>>;
    mainCardPricingInFlightRef: MutableRefObject<Record<string, Promise<unknown>>>;
    setMainCardPartnerCellState: Dispatch<
        SetStateAction<Record<string, MainCardPartnerCellState>>
    >;
    mainCardPartnerCellState: Record<string, MainCardPartnerCellState>;
}

export function useMainCardScenarioPricing({
    solarUtilityData,
    priceQuoteOverrides,
    mainCardPricingBodyRef,
    mainCardPricingInFlightRef,
    setMainCardPartnerCellState,
    mainCardPartnerCellState,
}: UseMainCardScenarioPricingArgs) {
    const utilityCompanyName = useOneButtonStore((s) => s.utilityCompanyName);
    const mainCardPricingBodyByScenarioRef = useRef<Record<string, unknown>>({});
    const mainCardSystemKwByScenarioRef = useRef<Record<string, number>>({});
    /** Latest scenario key per catalog card (for dashboard $ cells that only know cardKey). */
    const mainCardLatestScenarioKeyByCardRef = useRef<Record<string, string>>({});
    const prevUtilityCodeRef = useRef<string | null>(null);
    const numericOpportunityId = useOneButtonStore((s) => s.opportunityID);

    const resolveMainCardUtilityCode = useCallback((): string => {
        if (!solarUtilityData) return '';
        const base = (solarUtilityData.SolarUtility ?? {}) as Record<string, unknown>;
        return resolveUtilityCompanyCode(base, utilityCompanyName);
    }, [solarUtilityData, utilityCompanyName]);

    useEffect(() => {
        const code = resolveMainCardUtilityCode();
        if (prevUtilityCodeRef.current != null && prevUtilityCodeRef.current !== code) {
            mainCardPricingBodyRef.current = {};
            mainCardPricingInFlightRef.current = {};
            mainCardPricingBodyByScenarioRef.current = {};
            mainCardSystemKwByScenarioRef.current = {};
            mainCardLatestScenarioKeyByCardRef.current = {};
            setMainCardPartnerCellState({});
        }
        prevUtilityCodeRef.current = code;
    }, [
        resolveMainCardUtilityCode,
        mainCardPricingBodyRef,
        mainCardPricingInFlightRef,
        setMainCardPartnerCellState,
    ]);

    const fetchMainCardPricingBody = useCallback(
        async (cacheKey: string, systemSize: number, estProd: number) => {
            const cached = mainCardPricingBodyRef.current[cacheKey];
            if (cached !== undefined) return cached;
            const inflight = mainCardPricingInFlightRef.current[cacheKey];
            if (inflight) return inflight;

            const base = (solarUtilityData!.SolarUtility ?? {}) as Record<string, unknown>;
            const lowSlope = priceQuoteOverrides?.lowSlopeOverride ?? 0;
            const normalSlope = priceQuoteOverrides?.normalSlopeOverride ?? 0;
            const roofPitch =
                normalSlope > 0 && (lowSlope == null || lowSlope === 0)
                    ? 'Normal Slope'
                    : lowSlope > 0
                      ? 'Low Slope'
                      : 'Normal Slope';
            const lowRoof = Math.round(Number(lowSlope) || 0);
            const standardRoof = Math.round(Number(normalSlope) || 0);

            const pricingPayload = {
                state: String(base.StateId ?? ''),
                systemSize,
                estimatedAnnualProduction: Math.round(estProd),
                utilityCompanyCode: resolveUtilityCompanyCode(base, utilityCompanyName),
                batteryCount: 1,
                roofPitch,
                lowRoof,
                standardRoof,
                products: ['Solar'],
                opportunityId: numericOpportunityId,
            };

            const p = quoteServices
                .getPricing(pricingPayload)
                .then((response) => {
                    const body = response?.data;
                    mainCardPricingBodyRef.current[cacheKey] = body;
                    return body;
                })
                .finally(() => {
                    delete mainCardPricingInFlightRef.current[cacheKey];
                });
            mainCardPricingInFlightRef.current[cacheKey] = p;
            return p;
        },
        [
            solarUtilityData,
            utilityCompanyName,
            priceQuoteOverrides?.lowSlopeOverride,
            priceQuoteOverrides?.normalSlopeOverride,
            mainCardPricingBodyRef,
            mainCardPricingInFlightRef,
        ],
    );

    const triggerMainCardPartnerPricing = useCallback(
        async (cardKey: string, estProd: number | undefined, systemKw: string) => {
            if (!solarUtilityData) return;
            const estProdNum = typeof estProd === 'number' && Number.isFinite(estProd) ? estProd : NaN;
            const systemSize = Number(systemKw);
            const utilityCode = resolveMainCardUtilityCode();
            const scenarioKey = mainCardPricingScenarioKey(cardKey, estProdNum, systemKw, utilityCode);
            const cellKeyGl = scenarioKey ? `${scenarioKey}:GoodLeap` : `${cardKey}:GoodLeap`;
            const cellKeyIgs = scenarioKey ? `${scenarioKey}:IGS` : `${cardKey}:IGS`;
            const cellKeyLr = scenarioKey ? `${scenarioKey}:LightReach` : `${cardKey}:LightReach`;

            if (!scenarioKey || !Number.isFinite(systemSize) || systemSize <= 0 || !Number.isFinite(estProdNum) || estProdNum <= 0) {
                const err = {
                    status: 'error' as const,
                    errorDetail: 'Missing system size or production for this card',
                };
                setMainCardPartnerCellState((prev) => ({ ...prev, [cellKeyGl]: err, [cellKeyIgs]: err, [cellKeyLr]: err }));
                return;
            }

            mainCardLatestScenarioKeyByCardRef.current[cardKey] = scenarioKey;

            setMainCardPartnerCellState((prev) => ({
                ...prev,
                [cellKeyGl]: { status: 'loading' },
                [cellKeyIgs]: { status: 'loading' },
                [cellKeyLr]: { status: 'loading' },
            }));

            try {
                const body = await fetchMainCardPricingBody(scenarioKey, systemSize, estProdNum);
                mainCardPricingBodyByScenarioRef.current[scenarioKey] = body;
                mainCardSystemKwByScenarioRef.current[scenarioKey] = systemSize;
                const cells = buildTreeCutPartnerColumnCells(body, { systemSizeKw: systemSize });
                const glBlocks = cells.GoodLeap || [];
                const igsBlocks = cells.IGS || [];
                const lrBlocks = cells.LightReach || [];
                setMainCardPartnerCellState((prev) => ({
                    ...prev,
                    [cellKeyGl]:
                        glBlocks.length > 0
                            ? { status: 'ok', blocks: glBlocks }
                            : {
                                  status: 'error',
                                  errorDetail: partnerEpcUnavailableDetailFromApi(
                                      body,
                                      'GoodLeap',
                                      'No recommended GoodLeap Solar offer value',
                                  ),
                              },
                    [cellKeyIgs]:
                        igsBlocks.length > 0
                            ? { status: 'ok', blocks: igsBlocks }
                            : {
                                  status: 'error',
                                  errorDetail: partnerEpcUnavailableDetailFromApi(
                                      body,
                                      'IGS',
                                      'No IGS Solar offer value',
                                  ),
                              },
                    [cellKeyLr]:
                        lrBlocks.length > 0
                            ? { status: 'ok', blocks: lrBlocks }
                            : {
                                  status: 'error',
                                  errorDetail: partnerEpcUnavailableDetailFromApi(
                                      body,
                                      'LightReach',
                                      'No LightReach totalEpcPayment value',
                                  ),
                              },
                }));
            } catch (e: any) {
                if (quoteServices.isPricingRequestAborted(e)) return;
                const message = e?.response?.data?.message || e?.message || 'Pricing failed';
                const err = { status: 'error' as const, errorDetail: String(message) };
                setMainCardPartnerCellState((prev) => ({ ...prev, [cellKeyGl]: err, [cellKeyIgs]: err, [cellKeyLr]: err }));
            }
        },
        [solarUtilityData, resolveMainCardUtilityCode, fetchMainCardPricingBody, setMainCardPartnerCellState],
    );

    const ensureMainCardPricingForCard = useCallback(
        async (cardKey: string, estProd: number | undefined, systemKw: string): Promise<unknown | null> => {
            if (!solarUtilityData) return null;
            const scenarioKey = mainCardPricingScenarioKey(
                cardKey,
                estProd,
                systemKw,
                resolveMainCardUtilityCode(),
            );
            if (!scenarioKey) return null;
            const cached = mainCardPricingBodyByScenarioRef.current[scenarioKey];
            if (cached !== undefined) return cached;
            await triggerMainCardPartnerPricing(cardKey, estProd, systemKw);
            return mainCardPricingBodyByScenarioRef.current[scenarioKey] ?? null;
        },
        [solarUtilityData, resolveMainCardUtilityCode, triggerMainCardPartnerPricing],
    );

    const getDesignEpcByPartner = useCallback(
        (
            cardKey: string,
            estProd?: number,
            systemKw?: string,
        ): Record<EpcDisplayPartner, DesignEpcCell> => {
            const scenarioKey =
                systemKw != null
                    ? mainCardPricingScenarioKey(cardKey, estProd, systemKw, resolveMainCardUtilityCode())
                    : null;
            const lookupKey = scenarioKey ?? mainCardLatestScenarioKeyByCardRef.current[cardKey] ?? cardKey;
            const body = mainCardPricingBodyByScenarioRef.current[lookupKey];
            if (!body) {
                return {
                    GoodLeap: { status: 'none' },
                    IGS: { status: 'none' },
                    LightReach: { status: 'none' },
                };
            }
            const systemSizeKw = mainCardSystemKwByScenarioRef.current[lookupKey];
            return buildDesignEpcByPartner(body, { systemSizeKw });
        },
        [resolveMainCardUtilityCode],
    );

    const getMainCardPartnerCellState = useCallback(
        (
            cardKey: string,
            partner: EpcDisplayPartner,
            estProd?: number,
            systemKw?: string,
        ): MainCardPartnerCellState | undefined => {
            const scenarioKey =
                systemKw != null
                    ? mainCardPricingScenarioKey(cardKey, estProd, systemKw, resolveMainCardUtilityCode())
                    : null;
            const lookupKey = scenarioKey ?? mainCardLatestScenarioKeyByCardRef.current[cardKey];
            if (lookupKey) return mainCardPartnerCellState[`${lookupKey}:${partner}`];
            return mainCardPartnerCellState[`${cardKey}:${partner}`];
        },
        [mainCardPartnerCellState, resolveMainCardUtilityCode],
    );

    const isMainCardEpcLoading = useCallback(
        (cardKey: string, estProd?: number, systemKw?: string): boolean => {
            const scenarioKey =
                systemKw != null
                    ? mainCardPricingScenarioKey(cardKey, estProd, systemKw, resolveMainCardUtilityCode())
                    : mainCardLatestScenarioKeyByCardRef.current[cardKey];
            if (!scenarioKey) return false;
            for (const partner of ['GoodLeap', 'IGS', 'LightReach'] as const) {
                const cell = mainCardPartnerCellState[`${scenarioKey}:${partner}`];
                if (cell?.status === 'loading') return true;
            }
            return false;
        },
        [mainCardPartnerCellState, resolveMainCardUtilityCode],
    );

    const renderMainCardPartnerValue = useCallback(
        (cardKey: string, partner: 'GoodLeap' | 'IGS' | 'LightReach') => {
            const scenarioKey = mainCardLatestScenarioKeyByCardRef.current[cardKey];
            const cellKey = scenarioKey ? `${scenarioKey}:${partner}` : `${cardKey}:${partner}`;
            const cell = mainCardPartnerCellState[cellKey];
            if (!cell) return '—';
            if (cell.status === 'loading') return <Spin size="small" />;
            if (cell.status === 'ok' && cell.blocks.length > 0) {
                return (
                    <span className="editor-ai-tree-cutting-card__goodleap-value editor-ai-tree-cutting-card__goodleap-value--primary">
                        {cell.blocks[0]}
                    </span>
                );
            }
            if (cell.status === 'error') {
                const detail = cell.errorDetail || '';
                const isNotAvailable = /^No\s/i.test(detail);
                if (isNotAvailable) {
                    return (
                        <span className="editor-ai-tree-cutting-card__goodleap-muted">
                            N/A
                        </span>
                    );
                }
                return (
                    <Tooltip title={detail}>
                        <span
                            className="editor-ai-tree-cutting-card__goodleap-muted"
                            style={{ color: '#f97373', cursor: 'help' }}
                        >
                            !
                        </span>
                    </Tooltip>
                );
            }
            return '—';
        },
        [mainCardPartnerCellState],
    );

    return {
        triggerMainCardPartnerPricing,
        renderMainCardPartnerValue,
        ensureMainCardPricingForCard,
        getDesignEpcByPartner,
        getMainCardPartnerCellState,
        isMainCardEpcLoading,
    };
};
